import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ActivityIndicator } from 'react-native';
import * as Crypto from 'expo-crypto';
import { useTheme } from '../../styles/ThemeProvider';
import { supabase } from '../../lib/supabase';
import {
  IDENTITY_COPY,
  IdentityRequestError,
  answerItemIdentity,
  getItemIdentity,
  type IdentityOutcome,
  type ResolveQuestion,
} from '../../lib/itemIdentity';

/**
 * T1 structured identity question: one material axis, offered only as the valid answers the
 * backend computed. Selecting a chip never sends anything by itself. The scan review screen
 * sends the selection after the item is saved, and `IdentityQuestionCard` sends it from the
 * item screen.
 *
 * S3: with `onUnsure`, a "Not sure" chip records that no answer is given (`selected === null`);
 * saving never waits on an answer.
 */
interface IdentityQuestionProps {
  question: ResolveQuestion;
  selected?: string | null;
  disabled?: boolean;
  onSelect: (value: string) => void;
  onUnsure?: () => void;
}

function useChipStyles() {
  const theme = useTheme();
  return useMemo(
    () =>
      StyleSheet.create({
        container: { gap: theme.spacing.sm, marginTop: theme.spacing.xs },
        prompt: { ...theme.typography.sm, color: theme.colors.textSecondary },
        chips: { flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing.sm },
        chip: {
          borderWidth: 1,
          borderColor: theme.colors.border,
          borderRadius: theme.radius.radiusMd,
          paddingVertical: theme.spacing.xs,
          paddingHorizontal: theme.spacing.md,
          backgroundColor: theme.colors.backgroundSurface,
        },
        chipSelected: { borderColor: theme.colors.accent },
        chipText: { ...theme.typography.sm, color: theme.colors.textPrimary },
        chipTextSelected: { color: theme.colors.accent },
        error: { ...theme.typography.xs, color: theme.colors.textSecondary },
      }),
    [theme]
  );
}

export function IdentityQuestion({
  question,
  selected,
  disabled,
  onSelect,
  onUnsure,
}: IdentityQuestionProps) {
  const styles = useChipStyles();
  return (
    <View style={styles.container} testID="identity-question">
      <Text style={styles.prompt}>{IDENTITY_COPY.resolve(question.axis_label)}</Text>
      <View style={styles.chips}>
        {question.candidates.map((candidate) => {
          const isSelected = selected === candidate.value;
          return (
            <TouchableOpacity
              key={candidate.value}
              style={[styles.chip, isSelected && styles.chipSelected]}
              onPress={() => onSelect(candidate.value)}
              disabled={disabled}
              accessibilityRole="button"
              accessibilityState={{ selected: isSelected, disabled: !!disabled }}
              testID={`identity-answer-${candidate.value}`}
            >
              <Text style={[styles.chipText, isSelected && styles.chipTextSelected]}>
                {candidate.label}
              </Text>
            </TouchableOpacity>
          );
        })}
        {onUnsure && (
          <TouchableOpacity
            style={[styles.chip, selected === null && styles.chipSelected]}
            onPress={onUnsure}
            disabled={disabled}
            accessibilityRole="button"
            accessibilityState={{ selected: selected === null, disabled: !!disabled }}
            testID="identity-answer-unsure"
          >
            <Text style={[styles.chipText, selected === null && styles.chipTextSelected]}>
              Not sure
            </Text>
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
}

/**
 * Item-screen resolution for a saved item with an open identity question. It fetches the
 * question, sends the chosen answer, and calls `onResolved` so the screen reloads the item.
 */
export function IdentityQuestionCard({
  productId,
  onResolved,
}: {
  productId: string;
  onResolved: () => void;
}) {
  const styles = useChipStyles();
  const [question, setQuestion] = useState<ResolveQuestion | null>(null);
  const [revision, setRevision] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  // One request key per (revision, axis, value): a retry of the same answer reuses it, so the
  // backend treats it as the same request (idempotent); a different answer gets a new key.
  const requestKeys = useRef<Record<string, string>>({});

  const token = useCallback(async () => {
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token ?? null;
  }, []);

  const show = useCallback((identity: IdentityOutcome | null | undefined) => {
    setQuestion(identity?.question ?? null);
    setRevision(identity?.question_revision ?? null);
  }, []);

  const reload = useCallback(async () => {
    const accessToken = await token();
    if (!accessToken) return null;
    const response = await getItemIdentity(productId, accessToken);
    show(response.identity);
    return response.identity;
  }, [productId, show, token]);

  useEffect(() => {
    let active = true;
    reload().catch(() => {
      if (active) setFailed(true);
    });
    return () => {
      active = false;
    };
  }, [reload]);

  const answer = async (value: string) => {
    if (!question || !revision) return;
    const accessToken = await token();
    if (!accessToken) return;
    const slot = `${revision}|${question.axis}|${value}`;
    requestKeys.current[slot] = requestKeys.current[slot] ?? Crypto.randomUUID();
    setBusy(true);
    setFailed(false);
    try {
      const response = await answerItemIdentity(
        productId,
        {
          axis: question.axis,
          value,
          requestKey: requestKeys.current[slot],
          questionRevision: revision,
        },
        accessToken
      );
      if (response.identity?.question) {
        show(response.identity); // the next question replaces this one in place
      } else {
        show(null);
        onResolved();
      }
    } catch (error) {
      if (error instanceof IdentityRequestError && error.status === 409) {
        // Answered or replaced elsewhere: show the current state instead of retrying blindly.
        const current = await reload().catch(() => null);
        if (!current?.question) onResolved();
      } else {
        setFailed(true);
      }
    } finally {
      setBusy(false);
    }
  };

  if (!question)
    return failed ? <Text style={styles.error}>Couldn't load this question.</Text> : null;
  return (
    <View>
      <IdentityQuestion question={question} onSelect={answer} disabled={busy} />
      {busy && <ActivityIndicator size="small" />}
      {failed && <Text style={styles.error}>Couldn't save your answer. Please try again.</Text>}
    </View>
  );
}

export default IdentityQuestion;
